import settings from '@/constants/constants';
import { usePortalPartnerBrand } from '@/hooks/portal-partner-brand';
import { LOGIN_ROUTE } from '@/routes/route-paths';
import { Logger } from '@/utils/logger';
import { zodEmailValidate } from '@montugroup/circuit-shared';
import AuthService from '@montugroup/circuit-shared/services/authentication.service';
import { Box, LoadingButton, Stack, TextField, toast, Typography } from '@montugroup/design-system';
import { BrandThemeProvider } from '@montugroup/themes';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import { useMutation } from '@tanstack/react-query';
import type { SubmitHandler } from 'react-hook-form';
import { useForm, useWatch } from 'react-hook-form';
import {
  StyledAuthCardBackground,
  StyledAuthCardContainer,
  StyledAuthPageContainer,
  StyledAuthPageWrapper,
  StyledBackLinkContainer,
  StyledCardHalfContent,
  StyledCardHalfDecoration,
  StyledFormContainer,
  StyledLink,
  StyledMobileLogoContainer,
} from './authPageCustomer.styles';

const logger = new Logger('forgotPassword');

export interface ForgotPasswordFormData {
  email: string;
}

const resetPassword = async ({ email }: ForgotPasswordFormData) => {
  const response = await AuthService.resetPassword(email);
  if (response.status !== 200) {
    throw new Error('Failed to reset password');
  }
  return response;
};

export function ForgotPassword() {
  const { key: brandName, Icon: Bg, Logo } = usePortalPartnerBrand();
  const { mutate, isPending, isSuccess } = useMutation({
    mutationFn: resetPassword,
    onError: (error) => {
      logger.error('Reset password attempt failed', error);
      toast.error('Reset password attempt failed. Please, try again.');
    },
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    defaultValues: {
      email: '',
    },
  });

  const email = useWatch({ name: 'email', control });

  const onSubmit: SubmitHandler<ForgotPasswordFormData> = async (formData) => {
    mutate({
      email: formData.email.trim(),
    });
  };

  const shouldShowForm = !isSuccess || email === '';

  return (
    <BrandThemeProvider theme={brandName}>
      <StyledAuthPageWrapper>
        <StyledAuthPageContainer>
          <StyledAuthCardContainer>
            <StyledCardHalfDecoration>
              <StyledAuthCardBackground>
                <Logo width="118px" sx={{ position: 'absolute', top: '3.25rem', left: '3.25rem', height: 'auto' }} />
                <Bg sx={{ position: 'absolute', bottom: '4.652rem', right: '0' }} />
              </StyledAuthCardBackground>
            </StyledCardHalfDecoration>
            <StyledCardHalfContent>
              <StyledMobileLogoContainer>
                <a href={settings.marketing.site.url} target="_blank" rel="noopener noreferrer">
                  <Logo width="150px" height="auto" fillColor={(theme) => theme.palette.primary.main} />
                </a>
              </StyledMobileLogoContainer>
              <StyledFormContainer>
                <StyledBackLinkContainer>
                  <StyledLink to={LOGIN_ROUTE}>
                    <KeyboardArrowLeftIcon sx={{ marginLeft: '-0.5rem' }} />
                    <Typography fontWeight={500}>Back to Login</Typography>
                  </StyledLink>
                </StyledBackLinkContainer>
                <Box component="form" data-private>
                  <Typography variant="h2" marginBottom={4}>
                    {shouldShowForm ? 'Forgot Password' : 'Email Sent'}
                  </Typography>

                  {shouldShowForm ? (
                    <Stack gap={4}>
                      <Typography marginBottom={2}>
                        Forgot your account’s password? Enter your email address and we&apos;ll send you a recovery
                        link.
                      </Typography>
                      <TextField
                        {...register('email', {
                          required: 'Please enter your email',
                          validate: zodEmailValidate,
                        })}
                        error={Boolean(errors.email)}
                        helperText={errors.email?.message ?? ''}
                        type="text"
                        id="userEmail"
                        placeholder="Enter email"
                        label="Email"
                      />
                      <LoadingButton
                        id="btnSubmit"
                        variant="contained"
                        color="primary"
                        type="submit"
                        onClick={handleSubmit(onSubmit)}
                        loading={isPending}
                        data-testid="submit-button"
                      >
                        Submit
                      </LoadingButton>
                    </Stack>
                  ) : (
                    <Typography>
                      If your email is already registered, we&apos;ll email you instructions to reset your password.{' '}
                    </Typography>
                  )}
                </Box>
              </StyledFormContainer>
            </StyledCardHalfContent>
          </StyledAuthCardContainer>
        </StyledAuthPageContainer>
      </StyledAuthPageWrapper>
    </BrandThemeProvider>
  );
}

export default ForgotPassword;
